import { Texture } from "@/core/backends/webgl/texture"
import { mat4, vec2, vec4 } from "gl-matrix"
import { getCropMatrix, quantizeCrop } from "@/core/crop"
import { Adjustments } from "@/core/types"

export interface ImageState {
	crop: vec4
	cropMatrix: mat4
	cropMatrixInv: mat4
	imageSize: vec2
	croppedImageSize: vec2
	originalImageSize: vec2 // Size before orientation
}

export function createImageState(): ImageState {
	return {
		crop: vec4.create(),
		cropMatrix: mat4.create(),
		cropMatrixInv: mat4.create(),
		imageSize: vec2.create(),
		croppedImageSize: vec2.create(),
		originalImageSize: vec2.create(),
	}
}

export function computeImageState(
	inputTexture: Texture,
	adjustments: Adjustments,
	initialOrientation: number,
	imageState = createImageState(),
) {
	const { orientation, crop, cropStraighten, flipX, flipY } = adjustments
	const finalOrientation = initialOrientation + orientation

	let width = imageState.originalImageSize[0] || inputTexture.width
	let height = imageState.originalImageSize[1] || inputTexture.height

	if (width === 0) {
		width = 1
	}

	if (height === 0) {
		height = 1
	}

	if (Math.abs(finalOrientation) % 2) {
		vec2.set(imageState.imageSize, height, width)
	} else {
		vec2.set(imageState.imageSize, width, height)
	}

	quantizeCrop(imageState.crop, imageState.imageSize, crop)

	vec2.set(
		imageState.croppedImageSize,
		imageState.imageSize[0] * imageState.crop[2],
		imageState.imageSize[1] * imageState.crop[3],
	)

	vec2.set(imageState.originalImageSize, width, height)

	getCropMatrix(
		imageState.cropMatrix,
		imageState.imageSize,
		imageState.crop,
		cropStraighten,
		flipX,
		flipY,
		finalOrientation,
	)

	mat4.invert(imageState.cropMatrixInv, imageState.cropMatrix)

	return imageState
}
